import { usePermissibleLocationsQuery } from "./usePermissibleLocationsQuery";
import { useLocationRoomsQuery } from "./useLocationRoomsQuery";
import { useCalendarBookingsQuery } from "./useCalendarBookingsQuery";

interface UseCalendarDataOptions {
  locationSlug: string;
  date: Date | null;
  calendarType: "day" | "week";
}

/**
 * Orchestrator hook that combines all calendar-related queries
 *
 * Executes three queries in parallel:
 * 1. Permissible locations (based on member role)
 * 2. Available rooms (for the selected location)
 * 3. Calendar bookings (optimized for mobile/desktop and calendar type)
 *
 * Benefits:
 * - Parallel execution (no request waterfalls)
 * - Independent cache management per data type
 * - Granular refetch control
 * - Derived loading states
 *
 * @param locationSlug - The location slug to fetch data for
 * @param date - The date to fetch bookings for
 * @param calendarType - The calendar view type (day/week)
 */
export function useCalendarData({
  locationSlug,
  date,
  calendarType,
}: UseCalendarDataOptions) {
  // Parallel queries - React Query handles orchestration
  const locationsQuery = usePermissibleLocationsQuery();

  const roomsQuery = useLocationRoomsQuery({
    locationSlug,
    enabled: !!locationSlug,
  });

  const bookingsQuery = useCalendarBookingsQuery({
    locationSlug,
    date,
    calendarType,
    enabled: !!locationSlug && !!date,
  });

  // Derive loading states
  const isInitialLoading = locationsQuery.isLoading || roomsQuery.isLoading;
  const isBookingsLoading = bookingsQuery.isLoading;
  const isLoading = isInitialLoading || isBookingsLoading;

  return {
    // Data
    availableLocations: locationsQuery.data ?? null,
    availableRooms: roomsQuery.data ?? null,
    bookings: bookingsQuery.data ?? null,

    // Loading states
    isLoading,
    isLocationLoading: locationsQuery.isLoading,
    isRoomLoading: roomsQuery.isLoading,
    isDateLoading: bookingsQuery.isLoading,

    // Refetch functions
    refetchLocations: locationsQuery.refetch,
    refetchRooms: roomsQuery.refetch,
    refetchBookings: bookingsQuery.refetch,
    refetchAll: () => {
      locationsQuery.refetch();
      roomsQuery.refetch();
      bookingsQuery.refetch();
    },

    // Query objects (for advanced usage)
    queries: {
      locations: locationsQuery,
      rooms: roomsQuery,
      bookings: bookingsQuery,
    },
  };
}
